import React, { useEffect, useState, useCallback } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

interface StaffRow {
  id?: number;
  player_staff_id?: number;
  nom: string;
  prenom: string;
  statut_planning?: string;
}

interface CompetenceRow {
  config_competence_id: number;
  nom: string;
}

interface VehiculeCrewSectionProps {
  vehiculeId: number;
}

const VehiculeCrewSection: React.FC<VehiculeCrewSectionProps> = ({ vehiculeId }) => {
  const [crew, setCrew] = useState<StaffRow[]>([]);
  const [available, setAvailable] = useState<StaffRow[]>([]);
  const [requiredComps, setRequiredComps] = useState<CompetenceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getApiBaseUrl(true)}/buildings/vehicules/${vehiculeId}/crew`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) { setError(data.error || 'Erreur chargement équipage'); return; }
      setCrew(data.data.crew || []);
      setAvailable(data.data.available_staff || []);
      setRequiredComps(data.data.required_competences || []);
    } catch {
      setError('Erreur réseau');
    } finally {
      setLoading(false);
    }
  }, [vehiculeId]);

  useEffect(() => { void load(); }, [load]);

  const crewIds = new Set(crew.map(c => c.player_staff_id ?? c.id));

  const addStaff = async (staffId: number) => {
    setBusy(staffId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getApiBaseUrl(true)}/buildings/vehicules/${vehiculeId}/crew`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_staff_id: staffId }),
      });
      const data = await res.json();
      if (!data.success) { setError(data.error || 'Erreur ajout'); return; }
      await load();
    } catch { setError('Erreur réseau'); } finally { setBusy(null); }
  };

  const removeStaff = async (staffId: number) => {
    setBusy(staffId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${getApiBaseUrl(true)}/buildings/vehicules/${vehiculeId}/crew/${staffId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.success) { setError(data.error || 'Erreur suppression'); return; }
      await load();
    } catch { setError('Erreur réseau'); } finally { setBusy(null); }
  };

  const statusBadge = (s?: string) => {
    if (!s || s === 'en_garde') return <span className="text-xs text-green-400">En garde</span>;
    return <span className="text-xs text-red-400">Indisponible</span>;
  };

  if (loading) return <div className="text-zinc-400 text-sm py-2">Chargement équipage...</div>;

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold text-white mb-3">Équipage par défaut</h3>

      {requiredComps.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1">
          {requiredComps.map(c => (
            <span key={c.config_competence_id} className="px-2 py-0.5 bg-sky-500/20 text-sky-300 text-xs rounded">
              {c.nom}
            </span>
          ))}
        </div>
      )}

      {error && <p className="text-red-400 text-sm mb-2">{error}</p>}

      {/* Current crew */}
      {crew.length === 0 ? (
        <p className="text-zinc-400 text-sm mb-3">Aucun agent assigné. Sélectionnez dans la liste ci-dessous.</p>
      ) : (
        <div className="space-y-2 mb-4">
          {crew.map(s => {
            const sid = s.player_staff_id ?? s.id ?? 0;
            return (
              <div key={sid} className="flex items-center justify-between bg-zinc-800 rounded-lg px-4 py-2 border border-zinc-700">
                <div>
                  <span className="text-white font-medium">{s.prenom} {s.nom}</span>
                  <span className="ml-3">{statusBadge(s.statut_planning)}</span>
                </div>
                <button
                  onClick={() => removeStaff(sid)}
                  disabled={busy === sid}
                  className="text-red-400 hover:text-red-300 transition-colors text-sm disabled:opacity-50"
                >
                  Retirer
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Available staff not yet in crew */}
      {available.filter(s => !crewIds.has(s.id ?? 0)).length > 0 && (
        <>
          <p className="text-xs text-zinc-400 mb-2 uppercase tracking-wide">Personnel disponible dans ce bâtiment</p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {available.filter(s => !crewIds.has(s.id ?? 0)).map(s => (
              <div key={s.id} className="flex items-center justify-between bg-zinc-800/60 rounded px-3 py-1.5 border border-zinc-700/50">
                <div>
                  <span className="text-zinc-200 text-sm">{s.prenom} {s.nom}</span>
                  <span className="ml-2">{statusBadge(s.statut_planning)}</span>
                </div>
                <button
                  onClick={() => addStaff(s.id ?? 0)}
                  disabled={busy === s.id}
                  className="text-sky-400 hover:text-sky-300 transition-colors text-sm disabled:opacity-50"
                >
                  + Ajouter
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default VehiculeCrewSection;

